import { App, URLOpenListenerEvent } from "@capacitor/app";
import { ElitAuthProvider, checkPermissions } from "@elit-hair/elit-tools";
import { Button, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { accServiceBaseUrl } from "@/constants";

import useAuth from "./useAuth";

const LoadingScreen = () => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col justify-center items-center w-full h-screen bg-elitDarkBlue">
            <h1 className="text-white text-xl mb-4">{t("loading")}</h1>
            <CircularProgress color="secondary" size={"3rem"} />
        </div>
    );
};

const UnauthorizedScreen = () => {
    const { t } = useTranslation();

    const navigate = useNavigate();

    return (
        <div className="flex flex-col justify-center items-center w-full h-screen bg-elitDarkBlue">
            <h1 className="text-white">{t("unauthorized-page")}</h1>
            <p className="text-white">{t("sadly-you-are-not-authorized-to-view-this-page")}</p>
            <Button
                onClick={() => {
                    navigate("/");
                }}
                variant="contained"
                color="secondary"
            >
                {t("back-to-home")}
            </Button>
        </div>
    );
};

// For authentication in mobile, check useCapacitor hook
// There is the event triggered by cordova when the app is ready

export const ProtectedRoute = ({ children, permissions }: { children: React.ReactNode; permissions?: string[] }) => {
    const { i18n } = useTranslation();
    const { auth, setAuth, userData } = useAuth();

    const [authCode, setAuthCode] = useState<string | undefined>(undefined);
    useEffect(() => {
        const appUrlListener = (event: URLOpenListenerEvent) => {
            const url = new URL(event.url);

            setAuthCode(url.searchParams.get("auth_code") || "");
        };
        App.addListener("appUrlOpen", appUrlListener);
        return () => {
            App.removeAllListeners();
        };
    }, [setAuthCode]);

    const customLoginPage = import.meta.env.VITE_API_LOGIN_URL;
    return (
        <ElitAuthProvider
            provider="elitklinik"
            exchangeCodeUrl={`${accServiceBaseUrl}/auth/sso/exchange-code`}
            customValidationUrl={`${accServiceBaseUrl}/auth/sso/login`}
            customRedirectUrl={
                import.meta.env.PROD
                    ? import.meta.env.VITE_APP_URL || "https://transport.elitklinik.io/"
                    : import.meta.env.VITE_APP_URL || "http://localhost:3000"
            }
            authenticated={!!auth && !!userData}
            authorized={!!userData && checkPermissions(userData?.permissionCodes, permissions)}
            loginUrl={customLoginPage}
            style="C"
            appName="Transport Management App"
            lang={i18n.language}
            onAuthenticated={(accessToken, refreshToken) => {
                setAuth({ accessToken, refreshToken });
                setAuthCode(undefined);
            }}
            redirectingScreen={<LoadingScreen />}
            authenticatingScreen={<LoadingScreen />}
            unauthorizedScreen={<UnauthorizedScreen />}
            customAuthCode={authCode}
            authServices={[]}
        >
            {children}
        </ElitAuthProvider>
    );
};
